import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { api } from '../services/api';
import { useUiContent, UI_CONTENT_KEYS } from '../lib/uiContent';
import { toast } from 'sonner';
import { ShieldCheck, RefreshCw, CheckCircle2, XCircle, Layers3, KeyRound, Users, UserCog, Lock, Search, Plus, Trash2, Copy, Pencil } from 'lucide-react';
import { EmptyState, CardSkeleton, StatSkeletonGrid } from '../components/common/States';
import { StatCard } from '../components/common/StatCard';

// Centralized Access Control (Super Admin). Reads/writes the existing authorization
// engine via /api/access-control/* (+ /api/control for tenant group assignment).
// No engine behavior is modified — every change is applied live by the engine.

// Both lists are editable on Master → Platform Definitions; the fallbacks live
// in lib/uiContent so a failed fetch still renders a usable screen.

// Radix-based dropdown (avoids native <select>, which the dev visual-edits plugin
// breaks by wrapping <option>s in a <span> — invalid select content model).
function Dropdown({ value, onChange, options, className = 'w-48', placeholder }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={className}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{(options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
}
export default function SuperadminAccessControlPage({ view = 'dashboard', tenantId = 1 }) {
  const uiContent = useUiContent([UI_CONTENT_KEYS.ACCESS_PRECEDENCE, UI_CONTENT_KEYS.ACCESS_ROLE_OPTIONS]);
  const PRECEDENCE = uiContent[UI_CONTENT_KEYS.ACCESS_PRECEDENCE] || [];
  const ROLE_OPTIONS = uiContent[UI_CONTENT_KEYS.ACCESS_ROLE_OPTIONS] || [];
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState(null);
  const [stats, setStats] = useState(null);
  const [roles, setRoles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [perms, setPerms] = useState(null);
  const [audit, setAudit] = useState([]);
  const [tenants, setTenants] = useState([]);

  // Permission Groups CRUD
  const [groupSearch, setGroupSearch] = useState('');
  const [groupForm, setGroupForm] = useState(null); // null = closed; object = editing/creating

  // Tenant Assignment
  const [assignTenant, setAssignTenant] = useState(null);
  const [assignedKeys, setAssignedKeys] = useState([]);
  const [preview, setPreview] = useState(null);

  // Role Permissions editor
  const [rpRole, setRpRole] = useState('hr_manager');
  const [rpData, setRpData] = useState(null);
  const [rpChecked, setRpChecked] = useState([]);

  // User Permissions (overrides)
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [ovrForm, setOvrForm] = useState({ scope_type: 'action', scope_value: 'delete', effect: 'allow' });

  // Simulator
  const [simRole, setSimRole] = useState('employee');
  const [simResult, setSimResult] = useState(null);

  // Dynamic roles (incl. custom) for all dropdowns
  const [allRoles, setAllRoles] = useState([]);

  // Module Permissions (per-role) editor
  const [mpRole, setMpRole] = useState('hr_manager');
  const [mpData, setMpData] = useState(null);
  const [mpChecked, setMpChecked] = useState([]);
  const [mpInherit, setMpInherit] = useState(true);

  // Custom roles (Hierarchy tab) — created against a tenant, so it is chosen here.
  const [roleForm, setRoleForm] = useState(null); // null = closed; object = creating/editing

  // Every tenant-scoped read and write uses the tenant the workspace selected.
  // This was a hardcoded 1, so an operator editing what they believed were
  // tenant 12's permissions was writing to tenant 1's.
  const TENANT_ID = tenantId;
  const roleTenant = tenantId;
  const roleKeys = (allRoles.length ? allRoles.map(r => r.key) : ROLE_OPTIONS).filter(k => k !== 'superadmin');

  // roles for dropdowns (shared across role-permissions, simulator, module-permissions)
  const ensureRoles = useCallback(async () => {
    try { setAllRoles((await api.get('/api/access-control/roles')).data.roles || []); } catch { /* */ }
  }, []);
  useEffect(() => { if (['role-permissions', 'simulator', 'module-permissions', 'menu-permissions'].includes(view)) ensureRoles(); }, [view, ensureRoles]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      if (view === 'dashboard') { setOverview((await api.get('/api/access-control/overview')).data); setStats((await api.get('/api/access-control/dashboard-stats')).data); }
      else if (view === 'roles') {
        // Custom roles are per tenant, so the list is only complete with one named.
        setRoles((await api.get(`/api/access-control/roles?tenant_id=${roleTenant}`)).data.roles || []);
        setTenants((await api.get('/api/access-control/tenants')).data.tenants || []);
      }
      else if (view === 'permissions' || view === 'module-permissions' || view === 'menu-permissions') setPerms((await api.get('/api/access-control/permissions')).data);
      else if (view === 'audit') setAudit((await api.get('/api/access-control/audit-logs')).data.audit_logs || []);
      else if (view === 'user-permissions') setUsers((await api.get('/api/access-control/users?limit=100')).data.users || []);
      else if (view === 'permission-groups') setGroups((await api.get('/api/access-control/permission-groups')).data.permission_groups || []);
      else if (view === 'group-permissions') { setGroups((await api.get('/api/access-control/permission-groups')).data.permission_groups || []); setTenants((await api.get('/api/access-control/tenants')).data.tenants || []); }
    } catch (e) { setError(e?.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  }, [view, roleTenant]);
  useEffect(() => { load(); }, [load]);

  // ---- Role Permissions ----
  const loadRolePerms = useCallback(async (role) => {
    try { const d = (await api.get(`/api/access-control/roles/${role}/permissions?tenant_id=${TENANT_ID}`)).data; setRpData(d); setRpChecked(d.effective || []); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load role permissions'); }
  }, [TENANT_ID]);
  const saveRolePerms = async () => {
    try { await api.put(`/api/access-control/roles/${rpRole}/permissions`, { tenant_id: TENANT_ID, permissions: rpChecked, disabled_permissions: [] }); toast.success(`Saved permissions for ${rpRole}`); await loadRolePerms(rpRole); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
  };
  useEffect(() => { if (view === 'role-permissions') loadRolePerms(rpRole); }, [view, rpRole, loadRolePerms]);

  // ---- Module Permissions (per-role) ----
  const loadRoleModules = useCallback(async (role) => {
    try {
      const d = (await api.get(`/api/access-control/roles/${role}/modules?tenant_id=${TENANT_ID}`)).data;
      setMpData(d);
      setMpInherit(d.role_module_override === null);
      setMpChecked(d.role_module_override === null ? (d.tenant_modules || []) : (d.role_module_override || []));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load role modules'); }
  }, [TENANT_ID]);
  const saveRoleModules = async () => {
    try {
      await api.put(`/api/access-control/roles/${mpRole}/modules`, { tenant_id: TENANT_ID, modules: mpInherit ? null : mpChecked });
      toast.success(`Module access saved for ${mpRole}`); await loadRoleModules(mpRole);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
  };
  useEffect(() => { if (view === 'module-permissions' || view === 'menu-permissions') loadRoleModules(mpRole); }, [view, mpRole, loadRoleModules]);

  // ---- Permission Groups CRUD ----
  const saveGroup = async () => {
    if (!groupForm?.group_key || !groupForm?.display_name) { toast.error('Key and name are required'); return; }
    try { await api.post('/api/access-control/permission-groups', groupForm); toast.success('Permission group saved'); setGroupForm(null); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save group'); }
  };
  const deleteGroup = async (g) => {
    if (!window.confirm(`Delete permission group "${g.display_name}"? This cannot be undone.`)) return;
    try { await api.delete(`/api/access-control/permission-groups/${g.id}`); toast.success('Group deleted'); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to delete'); }
  };
  const duplicateGroup = (g) => setGroupForm({ group_key: `${g.group_key}_copy`, display_name: `${g.display_name} (Copy)`, description: g.description, permissions: [...(g.permissions || [])] });

  // ---- Tenant Assignment ----
  const loadAssignment = useCallback(async (tid) => {
    try { const d = (await api.get(`/api/access-control/tenants/${tid}/permission-groups`)).data; setAssignedKeys((d.permission_groups || []).map(g => g.group_key)); }
    catch (e) { toast.error('Failed to load assignment'); }
  }, []);
  const saveAssignment = async () => {
    if (!assignTenant) return;
    try { await api.put(`/api/control/tenants/${assignTenant.id}/permission-groups`, { permission_group_keys: assignedKeys }); toast.success(`Groups assigned to ${assignTenant.name}`); await loadAssignment(assignTenant.id); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to assign'); }
  };
  const loadPreview = async (tid) => {
    try { setPreview((await api.post('/api/access-control/simulate', { role: 'hr_manager', tenant_id: tid })).data.effective); }
    catch { setPreview(null); }
  };

  // ---- User overrides ----
  const loadOverrides = useCallback(async (uid) => { try { setOverrides((await api.get(`/api/access-control/users/${uid}/overrides`)).data.overrides || []); } catch { /* */ } }, []);
  const addOverride = async () => { try { await api.post(`/api/access-control/users/${selectedUser.id}/overrides`, ovrForm); toast.success('Override applied'); await loadOverrides(selectedUser.id); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };
  const removeOverride = async (id) => { try { await api.delete(`/api/access-control/users/${selectedUser.id}/overrides/${id}`); toast.success('Override removed'); await loadOverrides(selectedUser.id); } catch (e) { toast.error('Failed'); } };

  // ---- Roles: create / edit / delete (custom roles only) ----
  const newRole = (copyFrom = null) => setRoleForm({
    mode: 'create', role_key: copyFrom ? `${copyFrom}_custom` : '', display_name: '',
    description: '', copy_from: copyFrom,
  });
  const editRole = (r) => setRoleForm({
    mode: 'edit', role_key: r.key, display_name: r.name, description: r.description || '',
    is_enabled: r.is_enabled !== false,
  });
  const saveRole = async () => {
    const form = roleForm;
    if (!form?.display_name?.trim()) { toast.error('A role needs a name'); return; }
    try {
      if (form.mode === 'create') {
        await api.post('/api/access-control/roles', {
          tenant_id: roleTenant,
          role_key: form.role_key.trim().toLowerCase(),
          display_name: form.display_name.trim(),
          description: form.description?.trim() || null,
          copy_from: form.copy_from || null,
        });
        toast.success(`Role "${form.display_name}" created`);
      } else {
        await api.patch(`/api/access-control/roles/${form.role_key}`, {
          tenant_id: roleTenant,
          display_name: form.display_name.trim(),
          description: form.description?.trim() || '',
          is_enabled: form.is_enabled,
        });
        toast.success('Role updated');
      }
      setRoleForm(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save the role'); }
  };
  const deleteRole = async (r) => {
    if (!window.confirm(`Delete the role "${r.name}"? Users holding it must be reassigned first.`)) return;
    try { await api.delete(`/api/access-control/roles/${r.key}?tenant_id=${roleTenant}`); toast.success('Role deleted'); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete the role'); }
  };

  const runSimulation = async () => { try { setSimResult((await api.post('/api/access-control/simulate', { role: simRole, tenant_id: TENANT_ID })).data); } catch (e) { toast.error('Simulation failed'); } };

  const header = (title, subtitle, extra) => (
    <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
      <div className="flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary/10 text-primary shrink-0"><ShieldCheck className="h-5 w-5" /></div>
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-foreground">{title}</h1>
          {subtitle ? <p className="text-sm text-muted-foreground">{subtitle}</p> : null}
        </div>
      </div>
      <div className="flex items-center gap-2">{extra}<Button variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh</Button></div>
    </div>
  );

  if (error) return <div className="p-6"><Card><CardContent className="p-6 text-sm text-danger">{error}</CardContent></Card></div>;

  // ===== DASHBOARD (Permission Overview) =====
  if (view === 'dashboard') return (
    <div className="p-1">
      {header('Access Control', 'Centralized authorization — single source of truth')}
      {loading && !stats ? <StatSkeletonGrid count={8} /> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard icon={Users} label="Total Roles" value={stats?.total_roles ?? 0} tone="primary" />
            <StatCard icon={Layers3} label="Permission Groups" value={stats?.total_permission_groups ?? 0} tone="info" />
            <StatCard icon={KeyRound} label="Permission Actions" value={stats?.total_permissions ?? 0} tone="purple" />
            <StatCard icon={ShieldCheck} label="Modules" value={stats?.total_modules ?? 0} tone="success" />
            <StatCard icon={Users} label="Total Users" value={stats?.total_users ?? 0} tone="primary" />
            <StatCard icon={ShieldCheck} label="System Roles" value={stats?.system_roles ?? 0} tone="info" />
            <StatCard icon={ShieldCheck} label="Custom Roles" value={stats?.custom_roles ?? 0} tone="purple" />
            <StatCard icon={Users} label="Unassigned Users" value={stats?.unassigned_users ?? 0} tone="warning" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Card><CardHeader><CardTitle className="text-base">Users by Role</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(stats?.users_by_role || {}).map(([r, c]) => (
                  <div key={r} className="flex items-center justify-between text-sm rounded-md px-2.5 py-1.5 bg-muted/40"><span className="font-medium text-foreground">{r}</span><Badge variant="soft">{c}</Badge></div>
                ))}
                {!stats?.users_by_role ? <EmptyState compact icon={Users} title="No data" description="No user distribution available yet." /> : null}
              </CardContent></Card>
            <Card><CardHeader><CardTitle className="text-base">Authorization Precedence</CardTitle></CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {PRECEDENCE.map((p, i) => <Badge key={p} variant={i < 2 ? 'danger' : 'outline'}>{i + 1}. {p}</Badge>)}
                <p className="w-full text-xs text-muted-foreground mt-2">Engine: {overview?.engine}</p>
              </CardContent></Card>
          </div>
        </>
      )}
    </div>
  );

  // ===== ROLES (list + clone) =====
  if (view === 'roles') return (
    <div className="p-1">
      {header('Roles', 'System & custom roles, priority and hierarchy', (
        <Button size="sm" onClick={() => newRole()}><Plus className="h-4 w-4 mr-1" />New role</Button>
      ))}
      {roleForm && (
        <Card className="mb-4"><CardHeader><CardTitle className="text-base">
          {roleForm.mode === 'create' ? 'New custom role' : `Edit ${roleForm.role_key}`}
        </CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Role key</label>
                <input
                  className="w-full mt-1 px-3 py-2 text-sm rounded-md border border-border bg-background disabled:opacity-60"
                  value={roleForm.role_key}
                  disabled={roleForm.mode === 'edit'}
                  placeholder="regional_lead"
                  onChange={(e) => setRoleForm({ ...roleForm, role_key: e.target.value })}
                />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {roleForm.mode === 'edit'
                    ? 'The key is what users are stored against, so it cannot be renamed.'
                    : 'Lowercase letters, digits and underscores. This is what users will be stored against.'}
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Display name</label>
                <input
                  className="w-full mt-1 px-3 py-2 text-sm rounded-md border border-border bg-background"
                  value={roleForm.display_name}
                  placeholder="Regional Lead"
                  onChange={(e) => setRoleForm({ ...roleForm, display_name: e.target.value })}
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Description</label>
              <input
                className="w-full mt-1 px-3 py-2 text-sm rounded-md border border-border bg-background"
                value={roleForm.description}
                placeholder="What this role is for"
                onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
              />
            </div>
            {roleForm.copy_from && (
              <p className="text-xs text-muted-foreground">
                Starts with the permissions <span className="font-medium text-foreground">{roleForm.copy_from}</span> has in this tenant today.
              </p>
            )}
            {roleForm.mode === 'edit' && (
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="accent-primary" checked={roleForm.is_enabled}
                  onChange={(e) => setRoleForm({ ...roleForm, is_enabled: e.target.checked })} />
                Enabled — a disabled role grants nothing and cannot be assigned
              </label>
            )}
            <div className="flex gap-2">
              <Button size="sm" onClick={saveRole}>{roleForm.mode === 'create' ? 'Create role' : 'Save'}</Button>
              <Button size="sm" variant="outline" onClick={() => setRoleForm(null)}>Cancel</Button>
            </div>
          </CardContent></Card>
      )}
      {loading ? <CardSkeleton rows={6} /> : (
        <Card><CardContent className="p-0">
          {roles.length === 0 ? <EmptyState icon={ShieldCheck} title="No roles" description="No roles are defined yet." /> : (
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40 text-muted-foreground"><tr><th className="text-left font-medium p-3">Role</th><th className="text-left font-medium p-3">Priority</th><th className="text-left font-medium p-3">Parent (inheritance)</th><th className="text-left font-medium p-3">Type</th><th className="text-right font-medium p-3">Actions</th></tr></thead>
            <tbody>{roles.map(r => (
              <tr key={r.key} className="border-b border-border hover:bg-muted/30">
                <td className="p-3 font-medium text-foreground">{r.name}<div className="text-xs text-muted-foreground">{r.description}</div></td>
                <td className="p-3 tabular-nums">{r.priority}</td><td className="p-3">{r.parent || '—'}</td>
                <td className="p-3">
                  {r.is_system ? <Badge variant="info">system</Badge> : <Badge variant="soft">custom</Badge>}
                  {r.is_enabled === false && <Badge variant="outline" className="ml-1">disabled</Badge>}
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => newRole(r.key)}><Copy className="h-3 w-3 mr-1" />Duplicate</Button>
                  {!r.is_system && (
                    <>
                      <Button size="sm" variant="outline" className="ml-1" onClick={() => editRole(r)}><Pencil className="h-3 w-3 mr-1" />Edit</Button>
                      <Button size="sm" variant="outline" className="ml-1 text-danger" onClick={() => deleteRole(r)}><Trash2 className="h-3 w-3" /></Button>
                    </>
                  )}
                </td>
              </tr>))}</tbody>
          </table>
          )}
        </CardContent></Card>
      )}
    </div>
  );

  // ===== ROLE PERMISSIONS (assign verbs) =====
  if (view === 'role-permissions') {
    const catalog = rpData?.catalog || [];
    return (
      <div className="p-1">
        {header('Role Permissions', 'Assign permission verbs to a role — applied live by the engine')}
        <Card className="mb-4"><CardContent className="p-4 flex items-end gap-3 flex-wrap">
          <div><label className="text-xs text-muted-foreground block mb-1">Role</label>
            <Dropdown value={rpRole} onChange={setRpRole} options={roleKeys} /></div>
          <Button onClick={saveRolePerms}>Save Permissions</Button>
        </CardContent></Card>
        <Card><CardHeader><CardTitle className="text-base">Permissions for {rpRole}</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2.5">
            {catalog.length === 0 ? <CardSkeleton rows={5} className="w-full border-0 p-0" /> : catalog.map(p => {
              const on = rpChecked.includes(p);
              return (<label key={p} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${on ? 'bg-primary/10 border-primary text-primary font-medium' : 'bg-background border-border text-muted-foreground hover:bg-muted/50'}`}>
                <input type="checkbox" className="accent-primary" checked={on} onChange={() => setRpChecked(on ? rpChecked.filter(x => x !== p) : [...rpChecked, p])} />{p}</label>);
            })}
          </CardContent></Card>
      </div>
    );
  }

  // ===== PERMISSION GROUPS (full CRUD) =====
  if (view === 'permission-groups') {
    const filtered = groups.filter(g => !groupSearch || (g.display_name + g.group_key).toLowerCase().includes(groupSearch.toLowerCase()));
    return (
      <div className="p-1">
        {header('Permission Groups', 'Reusable permission sets', <Button size="sm" onClick={() => setGroupForm({ group_key: '', display_name: '', description: '', permissions: [] })}><Plus className="h-4 w-4 mr-1" />New Group</Button>)}
        <Card className="mb-3"><CardContent className="p-3"><div className="relative w-full md:w-72"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><input value={groupSearch} onChange={e => setGroupSearch(e.target.value)} placeholder="Search groups…" className="border border-border rounded-md pl-9 pr-3 py-2 text-sm bg-background w-full focus:outline-none focus:ring-2 focus:ring-ring" /></div></CardContent></Card>
        {groupForm && (
          <Card className="mb-4 border-primary ring-1 ring-primary/20"><CardHeader><CardTitle className="text-base">{groupForm.id ? 'Edit' : 'New'} Permission Group</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <div><label className="text-xs block mb-1">Key</label><input value={groupForm.group_key} disabled={!!groupForm.id} onChange={e => setGroupForm({ ...groupForm, group_key: e.target.value })} className="border rounded-md px-3 py-2 text-sm bg-background w-full" placeholder="payroll_managers" /></div>
                <div><label className="text-xs block mb-1">Display Name</label><input value={groupForm.display_name} onChange={e => setGroupForm({ ...groupForm, display_name: e.target.value })} className="border rounded-md px-3 py-2 text-sm bg-background w-full" placeholder="Payroll Managers" /></div>
              </div>
              <div><label className="text-xs block mb-1">Description</label><input value={groupForm.description || ''} onChange={e => setGroupForm({ ...groupForm, description: e.target.value })} className="border rounded-md px-3 py-2 text-sm bg-background w-full" /></div>
              <div><label className="text-xs block mb-1">Permissions (comma-separated, e.g. employees.read, payroll.create)</label>
                <input value={(groupForm.permissions || []).join(', ')} onChange={e => setGroupForm({ ...groupForm, permissions: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} className="border rounded-md px-3 py-2 text-sm bg-background w-full font-mono text-xs" /></div>
              <div className="flex gap-2"><Button onClick={saveGroup}>Save</Button><Button variant="outline" onClick={() => setGroupForm(null)}>Cancel</Button></div>
            </CardContent></Card>
        )}
        <Card><CardContent className="p-0">
          {loading ? <CardSkeleton rows={6} className="border-0" /> : filtered.length === 0 ? <EmptyState icon={KeyRound} title={groupSearch ? 'No matching groups' : 'No permission groups'} description={groupSearch ? 'Try a different search term.' : 'Create a reusable permission set to get started.'} actionLabel={groupSearch ? undefined : 'New Group'} onAction={groupSearch ? undefined : () => setGroupForm({ group_key: '', display_name: '', description: '', permissions: [] })} /> : (
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/40 text-muted-foreground"><tr><th className="text-left font-medium p-3">Group</th><th className="text-left font-medium p-3">Key</th><th className="text-left font-medium p-3">Type</th><th className="text-left font-medium p-3">Perms</th><th className="text-right font-medium p-3">Actions</th></tr></thead>
              <tbody>{filtered.map(g => (
                <tr key={g.id} className="border-b border-border hover:bg-muted/30">
                  <td className="p-3 font-medium text-foreground">{g.display_name}<div className="text-xs text-muted-foreground">{g.description}</div></td>
                  <td className="p-3 font-mono text-xs">{g.group_key}</td>
                  <td className="p-3">{g.is_system ? <Badge variant="info">system</Badge> : <Badge variant="soft">custom</Badge>}</td>
                  <td className="p-3 text-xs"><Badge variant="outline">{(g.permissions || []).length}</Badge></td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" className="mr-1" onClick={() => duplicateGroup(g)}><Copy className="h-3 w-3" /></Button>
                    {!g.is_system && <Button size="sm" variant="outline" className="mr-1" onClick={() => setGroupForm({ id: g.id, group_key: g.group_key, display_name: g.display_name, description: g.description, permissions: [...(g.permissions || [])] })}><Pencil className="h-3 w-3" /></Button>}
                    {!g.is_system && <Button size="sm" variant="outline" onClick={() => deleteGroup(g)}><Trash2 className="h-3 w-3" /></Button>}
                  </td>
                </tr>))}</tbody>
            </table>
          )}
        </CardContent></Card>
      </div>
    );
  }

  // ===== GROUP PERMISSIONS / TENANT ASSIGNMENT =====
  if (view === 'group-permissions') return (
    <div className="p-1">
      {header('Tenant Assignment', 'Assign permission groups to tenants — multi-select, applied live')}
      <div className="grid md:grid-cols-3 gap-4">
        <Card><CardHeader><CardTitle className="text-base">Tenants</CardTitle></CardHeader>
          <CardContent className="p-0 max-h-[460px] overflow-y-auto">
            {tenants.length === 0 ? <EmptyState compact icon={Users} title="No tenants" description="No tenants available to assign." /> : tenants.map(t => (
              <button key={t.id} onClick={() => { setAssignTenant(t); loadAssignment(t.id); loadPreview(t.id); }} className={`w-full text-left px-3 py-2 text-sm border-b border-border transition-colors hover:bg-muted ${assignTenant?.id === t.id ? 'bg-primary/10 border-l-2 border-l-primary' : ''}`}>
                <div className="font-medium text-foreground">{t.name}</div><div className="text-xs text-muted-foreground">{t.slug} · id {t.id}</div></button>
            ))}
          </CardContent></Card>
        <Card className="md:col-span-2"><CardHeader><CardTitle className="text-base">{assignTenant ? `Groups for ${assignTenant.name}` : 'Select a tenant'}</CardTitle></CardHeader>
          <CardContent>
            {!assignTenant ? <EmptyState icon={Users} title="Select a tenant" description="Choose a tenant to manage its permission groups." /> : (
              <>
                <div className="flex flex-wrap gap-2 mb-4">
                  {groups.length === 0 ? <EmptyState compact icon={KeyRound} title="No permission groups" description="No permission groups have been defined yet." /> : groups.map(g => {
                    const on = assignedKeys.includes(g.group_key);
                    return (<label key={g.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${on ? 'bg-primary/10 border-primary text-primary font-medium' : 'bg-background border-border text-muted-foreground hover:bg-muted/50'}`}>
                      <input type="checkbox" className="accent-primary" checked={on} onChange={() => setAssignedKeys(on ? assignedKeys.filter(k => k !== g.group_key) : [...assignedKeys, g.group_key])} />{g.display_name}</label>);
                  })}
                </div>
                <div className="flex gap-2 mb-4"><Button onClick={saveAssignment}>Save Assignment</Button>
                  <Button variant="outline" onClick={() => setAssignedKeys([])}>Clear all</Button></div>
                {preview && (
                  <div className="rounded-lg border border-border p-3 bg-muted/30 text-xs">
                    <div className="font-medium text-foreground mb-1">Effective preview (hr_manager in this tenant)</div>
                    <div className="text-muted-foreground">Modules: {preview.module_access.length} · Actions: {preview.actions.join(', ') || '—'}</div>
                  </div>
                )}
              </>
            )}
          </CardContent></Card>
      </div>
    </div>
  );

  // ===== PERMISSIONS catalog (read) =====
  if (view === 'permissions') return (
    <div className="p-1">
      {header('Permissions', 'module × action catalog (engine enforces module-access × role action verbs)')}
      {loading || !perms ? <div className="grid md:grid-cols-2 gap-4"><CardSkeleton rows={5} /><CardSkeleton rows={5} /></div> : (
        <div className="grid md:grid-cols-2 gap-4">
          <Card><CardHeader><CardTitle className="text-base">Actions ({perms.actions?.length || 0})</CardTitle></CardHeader>
            <CardContent className="flex flex-wrap gap-2">{(perms.actions || []).map(a => <Badge key={a} variant="outline">{a}</Badge>)}</CardContent></Card>
          <Card><CardHeader><CardTitle className="text-base">Modules ({perms.modules?.length || 0})</CardTitle></CardHeader>
            <CardContent className="flex flex-wrap gap-2">{(perms.modules || []).map(m => <Badge key={m} variant="soft">{m}</Badge>)}</CardContent></Card>
        </div>
      )}
    </div>
  );

  // ===== MODULE / MENU PERMISSIONS (configurable per role) =====
  if (view === 'module-permissions' || view === 'menu-permissions') {
    const catalog = mpData?.catalog || [];
    const isMenu = view === 'menu-permissions';
    return (
      <div className="p-1">
        {header(isMenu ? 'Menu Permissions' : 'Module Permissions', `Configure which modules ${isMenu ? '(and their menus) ' : ''}a role can access — applied live by the engine`)}
        <Card className="mb-4"><CardContent className="p-4 flex items-end gap-3 flex-wrap">
          <div><label className="text-xs text-muted-foreground block mb-1">Role</label>
            <Dropdown value={mpRole} onChange={setMpRole} options={roleKeys} /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={mpInherit} onChange={e => { setMpInherit(e.target.checked); if (e.target.checked) setMpChecked(mpData?.tenant_modules || []); }} />Inherit tenant modules (no per-role restriction)</label>
          <Button onClick={saveRoleModules}>Save Module Access</Button>
        </CardContent></Card>
        <Card><CardHeader><CardTitle className="text-base">Modules for {mpRole} {mpInherit ? <Badge variant="info" className="ml-2">inheriting tenant</Badge> : <Badge variant="soft" className="ml-2">restricted</Badge>}</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2.5">
            {catalog.length === 0 ? <CardSkeleton rows={5} className="w-full border-0 p-0" /> : catalog.map(m => {
              const tenantHas = (mpData?.tenant_modules || []).includes(m);
              const on = mpChecked.includes(m);
              return (
                <label key={m} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${mpInherit || !tenantHas ? 'opacity-50' : 'cursor-pointer'} ${on ? 'bg-primary/10 border-primary text-primary font-medium' : 'bg-background border-border text-muted-foreground hover:bg-muted/50'}`}>
                  <input type="checkbox" className="accent-primary" disabled={mpInherit || !tenantHas} checked={on}
                    onChange={() => setMpChecked(on ? mpChecked.filter(x => x !== m) : [...mpChecked, m])} />
                  {m}{!tenantHas ? <span className="text-xs text-muted-foreground">(not in tenant plan)</span> : null}
                </label>
              );
            })}
          </CardContent></Card>
        <p className="px-1 pt-2 text-xs text-muted-foreground">Action-level grants (view/create/update/…) are managed under <b>Role Permissions</b>; per-user exceptions under <b>User Permissions</b>.</p>
      </div>
    );
  }

  // ===== AUDIT LOGS =====
  if (view === 'audit') return (
    <div className="p-1">
      {header('Authorization Audit Logs', 'Recent permission & access events')}
      {loading ? <CardSkeleton rows={6} /> : (
        <Card><CardContent className="p-0">
          {audit.length === 0 ? <EmptyState icon={Lock} title="No audit events" description="No permission or access events have been recorded yet." /> : (
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/40 text-muted-foreground"><tr><th className="text-left font-medium p-3">Time</th><th className="text-left font-medium p-3">Actor</th><th className="text-left font-medium p-3">Action</th><th className="text-left font-medium p-3">Outcome</th><th className="text-left font-medium p-3">IP</th></tr></thead>
              <tbody>{audit.map(a => (
                <tr key={a.id} className="border-b border-border hover:bg-muted/30"><td className="p-3 text-xs text-muted-foreground">{a.timestamp}</td><td className="p-3">{a.actor}</td><td className="p-3 font-mono text-xs">{a.action}</td><td className="p-3">{a.outcome === 'success' ? <Badge variant="success">{a.outcome}</Badge> : <Badge variant="danger">{a.outcome}</Badge>}</td><td className="p-3 text-xs text-muted-foreground">{a.ip || '—'}</td></tr>))}</tbody>
            </table>
          )}
        </CardContent></Card>
      )}
    </div>
  );

  // ===== USER PERMISSIONS (override editor) =====
  if (view === 'user-permissions') return (
    <div className="p-1">
      {header('User Permissions', 'Per-user overrides — precedence #1 (deny wins). Applied live on top of role/group.')}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="md:col-span-1"><CardHeader><CardTitle className="text-base">Select User</CardTitle></CardHeader>
          <CardContent className="p-0 max-h-[460px] overflow-y-auto">
            {loading ? <CardSkeleton rows={6} className="border-0" /> : users.length === 0 ? <EmptyState compact icon={Users} title="No users" description="No users found to manage." /> : users.map(u => (
              <button key={u.id} onClick={() => { setSelectedUser(u); loadOverrides(u.id); }} className={`w-full text-left px-3 py-2 text-sm border-b border-border transition-colors hover:bg-muted ${selectedUser?.id === u.id ? 'bg-primary/10 border-l-2 border-l-primary' : ''}`}>
                <div className="font-medium text-foreground truncate">{u.email}</div><div className="text-xs text-muted-foreground">{u.role} · t{u.tenant_id}</div></button>
            ))}
          </CardContent></Card>
        <Card className="md:col-span-2"><CardHeader><CardTitle className="text-base">{selectedUser ? `Overrides — ${selectedUser.email}` : 'Select a user'}</CardTitle></CardHeader>
          <CardContent>
            {!selectedUser ? <EmptyState icon={UserCog} title="Select a user" description="Choose a user to manage per-user permission overrides." /> : (
              <>
                <div className="flex items-end gap-2 flex-wrap mb-4 p-3 rounded-lg border border-border bg-muted/30">
                  <div><label className="text-xs block mb-1">Scope</label><Dropdown className="w-32" value={ovrForm.scope_type} onChange={v => setOvrForm({ ...ovrForm, scope_type: v })} options={['action', 'module']} /></div>
                  <div><label className="text-xs block mb-1">Value</label><input value={ovrForm.scope_value} onChange={e => setOvrForm({ ...ovrForm, scope_value: e.target.value })} className="border rounded-md px-2 py-1.5 text-sm bg-background w-32" placeholder="delete / payroll" /></div>
                  <div><label className="text-xs block mb-1">Effect</label><Dropdown className="w-28" value={ovrForm.effect} onChange={v => setOvrForm({ ...ovrForm, effect: v })} options={['allow', 'deny']} /></div>
                  <Button size="sm" onClick={addOverride}>Add / Update</Button>
                </div>
                <table className="w-full text-sm">
                  <thead className="border-b bg-muted/40 text-muted-foreground"><tr><th className="text-left font-medium p-2">Scope</th><th className="text-left font-medium p-2">Value</th><th className="text-left font-medium p-2">Effect</th><th className="p-2"></th></tr></thead>
                  <tbody>{overrides.length === 0 ? <tr><td className="p-0" colSpan={4}><EmptyState compact icon={UserCog} title="No overrides" description="This user follows their role/group permissions." /></td></tr> : overrides.map(o => (
                    <tr key={o.id} className="border-b border-border hover:bg-muted/30"><td className="p-2">{o.scope_type}</td><td className="p-2 font-mono text-xs">{o.scope_value}</td><td className="p-2">{o.effect === 'deny' ? <Badge variant="danger">deny</Badge> : <Badge variant="success">allow</Badge>}</td><td className="p-2 text-right"><Button size="sm" variant="outline" onClick={() => removeOverride(o.id)}>Remove</Button></td></tr>))}</tbody>
                </table>
              </>
            )}
          </CardContent></Card>
      </div>
    </div>
  );

  // ===== PERMISSION SIMULATOR =====
  return (
    <div className="p-1">
      {header('Permission Simulator', 'Resolve effective permissions through the live engine — and explain why')}
      <Card className="mb-4"><CardContent className="p-4 flex items-end gap-3 flex-wrap">
        <div><label className="text-xs text-muted-foreground block mb-1">Role</label>
          <Dropdown value={simRole} onChange={setSimRole} options={[...roleKeys, 'superadmin']} /></div>
        <Button onClick={runSimulation}>Simulate</Button>
      </CardContent></Card>
      {simResult && (<>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <StatCard icon={ShieldCheck} label="Modules" value={simResult.effective.module_access.length} tone="info" />
          <StatCard icon={KeyRound} label="Actions" value={simResult.effective.actions.length} tone="purple" />
          <StatCard icon={CheckCircle2} label="Allowed" value={simResult.effective.allowed_count} tone="success" />
          <StatCard icon={XCircle} label="Denied" value={simResult.effective.denied_count} tone="danger" />
        </div>
        <Card><CardHeader><CardTitle className="text-base">Effective decisions ({simResult.decisions.length})</CardTitle></CardHeader>
          <CardContent className="p-0 max-h-[460px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/40 text-muted-foreground sticky top-0 z-10"><tr><th className="text-left font-medium p-3">Permission</th><th className="text-left font-medium p-3">Decision</th><th className="text-left font-medium p-3">Why</th></tr></thead>
              <tbody>{simResult.decisions.map(d => (
                <tr key={d.permission} className="border-b border-border hover:bg-muted/30">
                  <td className="p-3 font-mono text-xs">
                    <span className="inline-flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full ${d.allowed ? 'bg-primary' : 'bg-muted-foreground/40'}`} />
                      {d.permission}
                    </span>
                  </td>
                  <td className="p-3">{d.allowed ? <Badge variant="success">allow</Badge> : <Badge variant="danger">deny</Badge>}</td>
                  <td className="p-3 text-xs text-muted-foreground">{d.reason}</td></tr>))}</tbody>
            </table>
          </CardContent></Card>
      </>)}
    </div>
  );
}
